/**
 * Supplier Quotations — prototype `vSuppliers()`, contract §7, infra C.4.
 *
 * The waiting room between sending RFQs and comparing them: what went out, what has come
 * back, and anything that needs fixing before the comparison can be trusted — chiefly a
 * supplier who quoted in a different UOM than the RFQ asked for.
 *
 * A status screen, deliberately. Every received reply is in the comparison automatically —
 * there is nothing to admit or select, so the only action here is reminding a supplier who
 * has gone quiet. Deciding *who supplies what* happens on the comparison workbench, where
 * the rates can be seen side by side.
 *
 * FE/BE split:
 *   FE — the mismatch alert and its conversion hint, the rate restated per RFQ UOM, and the
 *        "sent N days ago" age (all derived in lib/sq.ts from data already fetched).
 *   BE — parsing the supplier's returned Excel into a quotation, and setting `uom_mismatch`;
 *        both are announced by the `sq.received` event.
 */

import { useMemo, useState } from 'react'
import { printedDescription } from '../../lib/internalNotes'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Badge } from '../../components/Badge'
import { ReadOnlyGate } from '../../components/ReadOnlyGate'
import { lockReason } from '../../lib/readOnly'
import { ContextBar } from '../../components/ContextBar'
import { Button, Card, PageHead, Placeholder } from '../../components/ui'
import { RecordQuotationModal } from './RecordQuotationModal'
import { QuoteGridModal } from './QuoteGridModal'
import { buildManualQuotation } from '../../lib/manualSupplierQuotation'
import { baseCurrencyFor } from '../../lib/api/baseCurrency'
import { getSessionCompany } from '../../lib/api/company'
import { useToast } from '../../components/Toast'
import { formatDate, formatTime } from '../../lib/format'
import { bestRateByItem } from '../../lib/quoteBenchmark'
import { useItemQuoteHistories } from '../../lib/api/useItemQuoteHistory'
import type { PurchaseHistoryEntry } from '../../lib/types'

/** Stable identity, so a pending batch does not hand the table a new Map every render. */
const EMPTY_HISTORIES: Map<string, PurchaseHistoryEntry[]> = new Map()
import { useEnquiry, useRfqs } from '../../lib/api/useEnquiries'
import { useSendRfqReminder } from '../../lib/api/useEnquiryMutations'
import { useItemsByCode } from '../../lib/api/useItems'
import { useCreateSupplierQuotation, useParseSupplierReply, useSupplierQuotations } from '../../lib/api/useSupplierQuotations'
import { qk } from '../../lib/api/keys'
import { collectUomMismatches, describeParseFailure, pairRepliesToRfqs, replyProgress, replyRowKey } from '../../lib/sq'
import { buildReminderDraft, daysAwaiting } from '../../lib/rfq'
import { useSupplierEmails } from '../../lib/api/useItems'
import { useRepliesByRfq } from '../../lib/api/useRfqReplies'
import { getCompany } from '../../lib/api/company'
import type { BuildQuotationResult } from '../../lib/supplierQuotationFromWorkbook'
import { RemindModal } from './RemindModal'
import type { RfqMessage } from '../../lib/types'
import { UomMismatchBanner } from './UomMismatchBanner'
import { AutoFileReply } from './AutoFileReply'
import { RfqTemplateCard } from './RfqTemplateCard'
import { InboundRepliesTable } from './InboundRepliesTable'
import { UnlinkedRepliesPanel } from './RfqRepliesPanel'
import { SupplierReplyPanel } from './SupplierReplyPanel'
import { duplicateSupplierQuotationOf } from '../../lib/duplicateSupplierQuotation'
import { deleteResource } from '../../lib/api/frappe'

export function SupplierQuotationsPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()

  const enquiryQuery = useEnquiry(id)
  const rfqsQuery = useRfqs(id)
  const quotationsQuery = useSupplierQuotations(id)

  const rfqs = rfqsQuery.data ?? []
  const quotations = quotationsQuery.data ?? []

  // Item master for every quoted line — one batch request, and the source of the UOM
  // conversion factors the mismatch hint needs.
  const quotedCodes = useMemo(
    () => quotations.flatMap((sq) => sq.items.map((item) => item.item_code)),
    [quotations],
  )

  // Each enquiry line's printed description by row number — what a quoted row is shown as
  // (`InboundRepliesTable.lineText`). Keyed on `cust_sno`, which IS `enquiry_row_no` on the wire.
  const enquiryLines = enquiryQuery.data?.items
  const lineText = useMemo(
    () => new Map((enquiryLines ?? []).map((l) => [l.cust_sno, printedDescription(l)])),
    [enquiryLines],
  )

  /**
   * Cheapest quote per item across every reply — computed here, where every quotation is already
   * loaded, and handed down so each line can say whether it is the good one. No extra request.
   */
  const benchmarks = useMemo(
    () =>
      bestRateByItem(
        quotations.flatMap((sq) =>
          sq.items.map((item) => ({
            item_code: item.item_code,
            rate: item.rate,
            // `Supplier.code` — the badge resolves it to a name through the same map the rows use.
            supplier: sq.supplier,
          })),
        ),
      ),
    [quotations],
  )
  const { byCode: itemsByCode } = useItemsByCode(quotedCodes)
  // One batched read for every quoted item's past prices — see `useItemQuoteHistories`.
  const quoteHistories = useItemQuoteHistories(quotedCodes)


  // Code -> display name, built from the RFQs the server DID resolve a name for — used to show
  // a human-readable supplier in the mismatch banner even though `SupplierQuotation.supplier`
  // itself is a bare code as of task 9 (see `lib/sq.ts`'s `collectUomMismatches`/`pairRepliesToRfqs`).
  const supplierNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const rfq of rfqs) if (rfq.supplier_name) map.set(rfq.supplier, rfq.supplier_name)
    return map
  }, [rfqs])

  /*
    Linked supplier mail for every RFQ on this enquiry, in ONE request. The table needs it per row
    — to stop saying "Awaiting" and stop offering Remind once a supplier has answered — and a hook
    cannot be called inside the row map, so it is fetched here and passed down as data.
  */
  const repliesByRfq = useRepliesByRfq(useMemo(() => rfqs.map((r) => r.id), [rfqs]))
  const rows = useMemo(
    () => pairRepliesToRfqs(rfqs, quotations, repliesByRfq.data ?? new Map()),
    [rfqs, quotations, repliesByRfq.data],
  )

  /** Whose correspondence is open in the docked panel, by ROW key — see `replyRowKey`: an RFQ id
   *  is not unique across rows when one supplier has quoted it several times. */
  const [openSupplierKey, setOpenSupplierKey] = useState<string | null>(null)
  const openSupplierRow = rows.find((r) => replyRowKey(r) === openSupplierKey) ?? null
  const mismatches = useMemo(
    () => collectUomMismatches(quotations, itemsByCode, supplierNames),
    [quotations, itemsByCode, supplierNames],
  )
  const progress = replyProgress(rows)

  /**
   * Remind opens a DRAFT rather than firing immediately.
   *
   * `rfq.remind_rfq` answers `{sent: true, sent_to: null}` — no recipient resolved, no timestamp
   * written, no `Communication` created (measured 2026-08-07). Wired to a button it reports
   * success and mails nobody, so the purchaser stops waiting for a reply that was never asked
   * for. The chase now goes through the same endpoint as the first send, with `type: 'reminder'`.
   */
  const [reminder, setReminder] = useState<{ rfq: string; draft: RfqMessage } | null>(null)

  // Real addresses come from `items.get_supplier_emails` — `Supplier.primary_email` is always
  // blank on this bench (defect B3), and the RFQ's own `supplier_email` is null on every row we
  // have seen. Same source the RFQ send flow trusts.
  const remindSupplier = reminder ? (rfqs.find((r) => r.id === reminder.rfq)?.supplier ?? '') : ''
  const supplierEmails = useSupplierEmails(remindSupplier ? [remindSupplier] : [])

  const sendReminder = useSendRfqReminder(id, {
    onSuccess: (result) => {
      setReminder(null)
      // With the time it went (Akhil, 2026-09-16) — the same stamp the row now shows.
      const when = result.reminded_at ?? result.sent_at
      toast(`Reminder sent to ${result.sent_to.join(', ')}${when ? ` — ${formatDate(when)} ${formatTime(when)}` : ''}`)
      if (result.stamp_warning) toast(result.stamp_warning)
    },
    onError: (error) => {
      if (error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: qk.supplierQuotations(id) })
        void queryClient.invalidateQueries({ queryKey: qk.rfqs(id) })
      }
      toast(
        error.status === 409
          ? 'That supplier has already replied — refreshing.'
          : `Could not send the reminder: ${error.message}`,
      )
    },
  })

  /**
   * Turn one linked inbound mail into a `Supplier Quotation` — explicitly, never on arrival.
   *
   * `parse_supplier_reply` cannot yet tell a quote from a refusal: against a "Sorry no stock"
   * reply it emitted a quotation carrying the RFQ's line at rate 0.00 and reported `matched: 1`
   * (ask `E12`). So the outcome is reported honestly here — including `matched: 0`, which means
   * the mail held nothing priceable and the purchaser should read it themselves.
   */
  const [parsingReplyId, setParsingReplyId] = useState<string | null>(null)

  /*
    Recording a price that never came through an RFQ — a phone call, a WhatsApp message, a walk-in.

    Until this existed the ONLY route into a `Supplier Quotation` was parsing an inbound mail
    against an RFQ, so those prices had nowhere to go: the comparison never saw them and the
    enquiry read as unanswered. `create_supplier_quotation` accepts an empty `rfq` (verified live
    2026-08-31), so no RFQ has to be invented to hold one.
  */
  const [recording, setRecording] = useState(false)
  /*
    `company` is mandatory on Supplier Quotation (server, 2026-09-17: "Value missing for Supplier
    Quotation: Company"). The enquiry's is the right one; an enquiry raised before the field
    existed has none, so the company the purchaser is operating as stands in — said in the toast,
    so the enquiry gets fixed — and with neither there is nothing honest to send.
  */
  const enquiryCompany = (enquiryQuery.data?.header.company || '').trim()
  const sqCompany = enquiryCompany || getSessionCompany() || ''
  const companyNote = enquiryQuery.data && enquiryCompany === '' && sqCompany ? ` (filed under ${sqCompany} — the enquiry has no company; set it on the RFQ builder header)` : ''
  const refuseWithoutCompany = (): boolean => {
    if (sqCompany !== '') return false
    toast('Cannot file a supplier quotation: this enquiry has no company and no operating company is selected. Set the company on the enquiry header (RFQ builder) first.')
    return true
  }
  /** The price grid — several suppliers at once (Akhil, 2026-09-16). See `QuoteGridModal`. */
  const [gridOpen, setGridOpen] = useState(false)
  const [gridSaving, setGridSaving] = useState(false)

  const createQuotation = useCreateSupplierQuotation({
    onSuccess: (name, payload) => {
      setParsingReplyId(null)
      void queryClient.invalidateQueries({ queryKey: ['rfq-replies'] })
      void queryClient.invalidateQueries({ queryKey: qk.rfqs(id) })
      // Neutral wording: this fires for a parsed workbook, a keyed-in quotation and a grid column alike.
      toast(`Filed ${name} for ${payload.supplier} — ${payload.items.length} line${payload.items.length === 1 ? '' : 's'}`)
    },
    onError: (error, payload) => {
      setParsingReplyId(null)
      /*
        One draft per RFQ (2026-09-19): the server refuses a second and cannot update the first
        (`duplicateSupplierQuotationOf`). A quotation filed wrong — rate 0.00 on ENQ-2026-00238 —
        would otherwise be stuck; the way through is to discard the draft and file this one.
      */
      const existing = duplicateSupplierQuotationOf(error.message)
      if (existing) {
        const ok = window.confirm(
          `${existing} already exists for this RFQ — the server allows one supplier quotation per RFQ and cannot update it.\n\n` +
            `Discard ${existing} and file this reply in its place? The old draft's prices are replaced by the ones read now.`,
        )
        if (ok) {
          setParsingReplyId(payload.rfq ?? null)
          void deleteResource('Supplier Quotation', existing)
            .then(() => createQuotation.mutate(payload))
            .catch((e: unknown) => {
              setParsingReplyId(null)
              toast(`Could not discard ${existing}: ${e instanceof Error ? e.message : String(e)}`)
            })
          return
        }
      }
      toast(`Could not file that quotation: ${error.message}`)
    },
  })
  const parseReply = useParseSupplierReply(id, {
    onSuccess: (result) => {
      setParsingReplyId(null)
      void queryClient.invalidateQueries({ queryKey: ['rfq-replies'] })
      toast(
        result.matched === 0
          ? 'Nothing priceable in that reply — read it and record the quotation by hand if needed.'
          : `Read ${result.matched} line${result.matched === 1 ? '' : 's'}` +
            (result.unmatched > 0 ? ` · ${result.unmatched} could not be matched to an enquiry line` : ''),
      )
    },
    onError: (error) => {
      setParsingReplyId(null)
      // A known, filed server defect gets its own explanation; anything else keeps the server's
      // own words rather than a guess that would send someone looking in the wrong place.
      toast(describeParseFailure(error.message) ?? `Could not read that reply: ${error.message}`)
    },
  })

  /**
   * File a quotation from the workbook the CLIENT read, rather than asking the server to re-read it.
   *
   * `parse_supplier_reply` has rejected two of the three real supplier replies this bench has ever
   * received — `B4` then `B5`, both on the Delivery Time column our own template asks them to fill
   * in. The prices were legible in the browser both times. So where we can read the file, we build
   * the document and `create_supplier_quotation` records it; where we cannot (a prose reply, a
   * supplier's own format), the server's parser is still the only thing that can try.
   */
  const handleFileQuotation = (communication: string, result: BuildQuotationResult) => {
    if (!result.ok) {
      toast(`Can't file that as a quotation: ${result.reason}`)
      return
    }
    setParsingReplyId(communication)
    createQuotation.mutate(result.payload)
  }

  const handleParseReply = (rfqId: string, communication: string) => {
    setParsingReplyId(communication)
    parseReply.mutate({ rfq_id: rfqId, communication })
  }

  const openReminder = (rfqId: string) => {
    const rfq = rfqs.find((r) => r.id === rfqId)
    if (!rfq) return
    setReminder({
      rfq: rfqId,
      draft: buildReminderDraft({
        rfq: rfqId,
        enquiryId: id,
        supplierName: rfq.supplier_name || rfq.supplier,
        // Filled in from `useSupplierEmails` once it resolves — see the effect-free merge below.
        to: [],
        // The purchaser is on CC of EVERY RFQ mail, a chase included (Akhil, 2026-09-17: "always
        // add cc — purchaser"). The chases of 12:23 that morning went out with none.
        cc: enquiry?.header.purchaser ? [enquiry.header.purchaser] : [],
        sentAt: rfq.sent_at,
        now: new Date(),
      }),
    })
  }

  // Merge the resolved addresses in at render time rather than in an effect: the query may settle
  // after the modal opens, and an effect would need a dependency on a value the user can edit.
  const resolved = remindSupplier ? supplierEmails.data?.[remindSupplier] : undefined
  const reminderDraft: RfqMessage | null = reminder
    ? { ...reminder.draft, to: reminder.draft.to.length > 0 ? reminder.draft.to : (resolved?.to ?? []) }
    : null

  /* ---------- states ---------- */

  if (enquiryQuery.isPending) return <Placeholder>Loading requirement…</Placeholder>

  if (enquiryQuery.error || !enquiryQuery.data) {
    const error = enquiryQuery.error as Error | null
    return (
      <Placeholder>
        <b>Could not load {id}.</b> Check the mock backend is running (<b>npm run dev:all</b>).
        {error ? <div style={{ marginTop: 6 }}>{error.message}</div> : null}
      </Placeholder>
    )
  }

  const enquiry = enquiryQuery.data
  const loadError = (rfqsQuery.error ?? quotationsQuery.error) as Error | null

  return (
    <>
      <ContextBar enquiry={enquiry} active="suppliers" />
      {/* Every control below is disabled once the enquiry is decided — `lib/readOnly.ts`. */}
      <ReadOnlyGate reason={lockReason(enquiry.status, 'suppliers')}>

      <PageHead help="suppliers"
        title="Supplier Quotations"
        subtitle={
          <>
            RFQs sent as protected Excel; supplier replies parse back into supplier quotations and enter the
            comparison automatically. If a supplier quotes in a <b>different UOM</b> than the RFQ, it's flagged
            here — the workbench converts it, or leaves it out of the ranking rather than compare it wrongly.
          </>
        }
      />

      {/*
        Beside the head rather than buried in a row: this is how a quotation gets recorded when the
        supplier did not email one, which on this bench is most of the time — `parse_supplier_reply`
        has rejected two of the three real replies ever received (`B4`, `B5`).
      */}
      <div className="row" style={{ marginBottom: 12 }}>
        <Button variant="primary" disabled={!enquiry} onClick={() => setGridOpen(true)}>
          ▦ Record prices — grid
        </Button>
        <Button disabled={!enquiry} onClick={() => setRecording(true)}>
          ✎ One supplier's quotation
        </Button>
        <span style={{ color: 'var(--muted)', fontSize: 12.5, alignSelf: 'center' }}>
          Prices given by phone or WhatsApp — no RFQ is sent. The grid takes several suppliers at once.
        </span>
      </div>

      {gridOpen && enquiry ? (
        <QuoteGridModal
          lines={enquiry.items}
          quotations={quotations}
          rfqs={rfqs}
          nameByCode={new Map([...itemsByCode].map(([code, item]) => [code, item.name]))}
          supplierNames={supplierNames}
          baseCurrency={baseCurrencyFor(enquiry.header.company)}
          company={enquiry.header.company}
          saving={gridSaving}
          onClose={() => setGridOpen(false)}
          onSave={async (entries, transactionDate) => {
            if (refuseWithoutCompany()) return
            // One ordinary Supplier Quotation per column, filed in turn; a failure names its
            // supplier and the rest still go.
            setGridSaving(true)
            let filed = 0
            const failed: string[] = []
            for (const entry of entries) {
              const built = buildManualQuotation({
                enquiryId: id,
                supplier: entry.supplier,
                company: sqCompany,
                currency: entry.currency,
                transactionDate,
                lines: enquiry.items,
                rates: entry.rates,
                remarks: entry.remarks,
              })
              if (!built.ok) {
                failed.push(`${entry.supplier}: ${built.reason}`)
                continue
              }
              try {
                await createQuotation.mutateAsync(built.payload)
                filed += 1
              } catch (error) {
                failed.push(`${entry.supplier}: ${error instanceof Error ? error.message : String(error)}`)
              }
            }
            setGridSaving(false)
            if (failed.length === 0) setGridOpen(false)
            toast(`${filed} quotation(s) filed${companyNote}${failed.length > 0 ? ` · not filed — ${failed.join('; ')}` : ''}`)
          }}
        />
      ) : null}

      {recording && enquiry ? (
        <RecordQuotationModal
          lines={enquiry.items}
          baseCurrency={baseCurrencyFor(enquiry.header.company)}
          company={enquiry.header.company}
          saving={createQuotation.isPending}
          onClose={() => setRecording(false)}
          onSave={({ supplier, currency, transactionDate, rates, remarks }) => {
            if (refuseWithoutCompany()) return
            const built = buildManualQuotation({
              enquiryId: id,
              supplier,
              company: sqCompany,
              currency,
              transactionDate,
              lines: enquiry.items,
              rates,
              remarks,
            })
            if (!built.ok) {
              toast(built.reason)
              return
            }
            setRecording(false)
            createQuotation.mutate(built.payload)
          }}
        />
      ) : null}

      {loadError ? (
        <Placeholder>
          <b>Could not load the quotations.</b>
          <div style={{ marginTop: 6 }}>{loadError.message}</div>
        </Placeholder>
      ) : null}

      <UomMismatchBanner mismatches={mismatches} />

      {rows.length > 0 ? (
        <div className="banner">
          📨 <b>{progress.sent} RFQ(s)</b> sent ·{' '}
          <Badge tone={progress.received > 0 ? 'ok' : 'muted'}>{progress.received} quoted</Badge>{' '}
          {/* Counted apart from "awaiting": these suppliers HAVE answered, and folding them in told
              the purchaser nobody had replied while the reply sat on screen. */}
          {progress.replied > 0 ? <Badge tone="info">{progress.replied} replied, not read</Badge> : null}{' '}
          {progress.awaiting > 0 ? <Badge tone="warn">{progress.awaiting} awaiting</Badge> : null} · a reply becomes
          a quotation when you read it; use <b>Remind</b> to chase a supplier who hasn't answered.
        </div>
      ) : null}

      <RfqTemplateCard rfqs={rfqs} />

      {rfqsQuery.isPending || quotationsQuery.isPending ? (
        <Placeholder>Loading supplier replies…</Placeholder>
      ) : rows.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 34, borderStyle: 'dashed' }}>
          <div style={{ fontSize: 28 }}>📤</div>
          <div style={{ fontWeight: 800, margin: '8px 0 4px' }}>No RFQs sent yet</div>
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>
            Assign suppliers to the RFQ lines in{' '}
            <span className="linkish" onClick={() => navigate(`/rfq/${id}`)}>
              item triage
            </span>{' '}
            and generate the RFQs — replies will land here.
          </div>
        </Card>
      ) : (
        <>
        <UnlinkedRepliesPanel
          enquiryId={id}
          linkedIds={[]}
          rfqChoices={rfqs.map((r) => ({ id: r.id, supplier: r.supplier_name || r.supplier }))}
          parsingReplyId={parsingReplyId}
          onParse={handleParseReply}
        />
        <InboundRepliesTable
            benchmarks={benchmarks}
            quoteHistories={quoteHistories.data ?? EMPTY_HISTORIES}
            quoteHistoryFailed={quoteHistories.isError}
          rows={rows}
          itemsByCode={itemsByCode}
          lineText={lineText}
          onRemind={openReminder}
          remindingRfqId={sendReminder.isPending ? (reminder?.rfq ?? null) : null}
          onOpenSupplier={setOpenSupplierKey}
          openSupplierRowKey={openSupplierKey}
        />
        </>
      )}

      {/*
        Files each supplier's reply as it arrives, instead of waiting for someone to open it.

        One child per unfiled reply — a component rather than a loop inside a hook, because each
        needs its own parsed workbook and hooks cannot be called in a loop. They render nothing.

        Only rows with NO quotation yet: a supplier who has already been filed is never touched,
        so re-opening this page cannot duplicate anything. `enabled` goes false while a manual
        file or parse is in flight so the two paths cannot race for one reply.
      */}
      {rows
        .filter((row) => row.quotation === null && row.replies.length > 0)
        .map((row) => (
          <AutoFileReply
            key={replyRowKey(row)}
            rfq={row.rfq}
            // Newest first (`pairRepliesToRfqs`), so this is their latest word on the RFQ.
            reply={row.replies[0]}
            enquiryId={id}
            company={sqCompany || getCompany()}
            fallbackCurrency={baseCurrencyFor(enquiry.header.company)}
            enabled={parsingReplyId === null && !createQuotation.isPending}
            enquiryLines={enquiry.items}
            onFile={handleFileQuotation}
          />
        ))}

      {/* The supplier's correspondence, docked beside the table rather than inside it — see
          `InboundRepliesTable`'s header for why the mail never expands the row. */}
      {openSupplierRow ? (
        <SupplierReplyPanel
          rfq={openSupplierRow.rfq}
          enquiryId={id}
          company={enquiry.header.company || getCompany()}
          supplierName={openSupplierRow.rfq.supplier_name ?? openSupplierRow.rfq.supplier}
          enquiryLines={enquiry.items}
          replies={openSupplierRow.replies}
          parsingReplyId={parsingReplyId}
          onFile={handleFileQuotation}
          onParse={handleParseReply}
          onClose={() => setOpenSupplierKey(null)}
        />
      ) : null}

      {/* Only once something is actually IN the comparison. With nothing quoted this read "All 0
          received replies are already in the comparison", directly after the banner said a supplier
          had replied — two sentences contradicting each other about the same enquiry. */}
      <div className="note" style={{ marginTop: 14 }}>
        {progress.received > 0 ? (
          <>
            💡 All {progress.received} quoted {progress.received === 1 ? 'reply is' : 'replies are'} already in the
            comparison — multi-supplier coverage &amp; blended pricing is on the{' '}
            <span className="linkish" onClick={() => navigate(`/compare/${id}`)}>
              Quote Comparison
            </span>{' '}
            workbench. Supplier <b>offer remarks</b> carry through from the SQ.
          </>
        ) : progress.replied > 0 ? (
          <>
            💡 Nothing is in the comparison yet. Read {progress.replied === 1 ? 'the reply' : 'a reply'} above as a
            quotation to bring {progress.replied === 1 ? 'those prices' : 'their prices'} into the{' '}
            <span className="linkish" onClick={() => navigate(`/compare/${id}`)}>
              Quote Comparison
            </span>{' '}
            workbench.
          </>
        ) : (
          <>
            💡 Nothing has come back yet. Once a supplier replies and the reply is read as a quotation, it joins the{' '}
            <span className="linkish" onClick={() => navigate(`/compare/${id}`)}>
              Quote Comparison
            </span>{' '}
            workbench automatically.
          </>
        )}
      </div>
      {reminderDraft && reminder ? (
        <RemindModal
          draft={reminderDraft}
          rfq={reminder.rfq}
          enquiryId={id}
          awaitingDays={daysAwaiting(rfqs.find((r) => r.id === reminder.rfq)?.sent_at, new Date())}
          excelUrl={rfqs.find((r) => r.id === reminder.rfq)?.excel_url ?? null}
          sending={sendReminder.isPending}
          onChange={(patch) => setReminder((r) => (r ? { ...r, draft: { ...r.draft, ...patch } } : r))}
          onClose={() => setReminder(null)}
          onSend={() =>
            sendReminder.mutate({
              rfq: reminder.rfq,
              to: reminderDraft.to,
              cc: reminderDraft.cc,
              subject: reminderDraft.subject,
              note: reminderDraft.note,
              // So the chase is recorded as a chase and the original send time survives it.
              chase: { original_sent_at: rfqs.find((r) => r.id === reminder.rfq)?.sent_at ?? null },
            })
          }
        />
      ) : null}

      </ReadOnlyGate>
    </>
  )
}
